import React, { useState } from 'react';
import { 
  Button, 
  Badge, 
  LeadStatusBadge, 
  ProjectStatusBadge, 
  StatCard, 
  ProgressBar, 
  EmptyState, 
  SearchBar, 
  Modal, 
  FormInput, 
  FormSelect, 
  FormTextarea, 
  FileUpload,
  Tabs, 
  PageTitle, 
  SectionTitle, 
  SectionSubtitle, 
  Label, 
  BodyText, 
  SubText, 
  HighlightText, 
  DonutWithLegend,
  ConfirmationModal,
  Toast
} from '../components/ui';
import toast from 'react-hot-toast';
import { 
  Sparkles, 
  Code, 
  Layers, 
  Sliders, 
  Type, 
  Play, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  AlertCircle,
  Eye, 
  HelpCircle, 
  Database,
  ArrowRight,
  TrendingUp,
  FileCheck,
  Zap,
  Search,
  Bell,
  Trash2,
  Lock,
  Plus
} from 'lucide-react';

export const DesignSystemPage = () => {
  // State for interactive elements
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  
  // Form input states
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState('designer');
  const [formBio, setFormBio] = useState('');
  const [formError, setFormError] = useState('');

  // Expandable stat card state
  const [cardExpanded, setCardExpanded] = useState(false);

  // Mock data for donut chart
  const donutData = [
    { name: 'EP Materials', value: 450 },
    { name: 'Non-Standard Items', value: 300 },
    { name: 'Labor & Trade Cost', value: 200 },
    { name: 'Miscellaneous', value: 100 },
  ];

  // Components list for live searching in the showcase
  const componentsList = [
    { name: 'Button', category: 'Interactions', desc: 'Sleek buttons with loading, hover, focus, and disabled states.' },
    { name: 'Badge', category: 'Interactions', desc: 'Pill status badge with optional dot indicators and standard variants.' },
    { name: 'LeadStatusBadge', category: 'Statuses', desc: 'Pre-mapped badges for CRM lead statuses (New, Interested, converted, etc.).' },
    { name: 'ProjectStatusBadge', category: 'Statuses', desc: 'Pre-mapped badges for project milestones (Planning, In Progress, Completed).' },
    { name: 'StatCard', category: 'Metrics', desc: 'KPI metric card supporting compact, dashboard, and expandable charts.' },
    { name: 'ProgressBar', category: 'Feedback', desc: 'Sleek rounded progress visualizer with shadow effect.' },
    { name: 'EmptyState', category: 'Feedback', desc: 'Polished graphic placeholder for blank data views.' },
    { name: 'SearchBar', category: 'Inputs', desc: 'Centralized search input with automatic clear button.' },
    { name: 'Modal', category: 'Overlays', desc: 'React Portal-mounted screen overlay with glassmorphism blur and ESC handling.' },
    { name: 'FormInput', category: 'Inputs', desc: 'Clean validated text, password, or email field.' },
    { name: 'FileUpload', category: 'Inputs', desc: 'Beautiful drag-and-drop file upload zone with micro-animations.' },
  ];

  const filteredComponents = componentsList.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Copy helper
  const handleCopyToken = (text, tokenType) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${tokenType}: ${text}`, {
      style: { background: '#1A1A1A', color: '#FFF', borderRadius: '8px' }
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formName) {
      setFormError('Name is required!');
      toast.error('Please fix form validation errors.');
      return;
    }
    setFormError('');
    toast.success(`Profile saved successfully for ${formName}!`);
    setIsModalOpen(false);
  };

  const tabsConfig = [
    { id: 'overview', label: '🎨 Design System Tokens' },
    { id: 'components', label: '🧱 Basic UI Components' },
    { id: 'forms', label: '📝 Inputs & Sandboxes' },
  ];

  return (
    <div className="space-y-6 pb-12 animate-fade-in">
      {/* Premium Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blum-dark via-blum-dark-soft to-neutral-900 text-white p-6 sm:p-8 shadow-xl border border-neutral-800">
        {/* Glow Effects */}
        <div className="absolute right-0 top-0 w-72 h-72 bg-blum-orange opacity-20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute left-1/3 bottom-0 w-60 h-60 bg-blue-600 opacity-10 blur-[80px] rounded-full pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-blum-orange/15 text-blum-orange border border-blum-orange/30 uppercase tracking-widest animate-pulse-orange">
              <Sparkles size={11} /> Unified Platform Active
            </span>
            <PageTitle className="text-white text-3xl font-extrabold tracking-tight font-display">
              Verve Design System
            </PageTitle>
            <p className="text-[13px] text-neutral-300 font-medium leading-relaxed font-body">
              A comprehensive library of pixel-perfect, highly responsive design patterns. Centralized in the <code className="text-blum-orange bg-white/5 px-1.5 py-0.5 rounded font-mono text-xs">components/ui/</code> folder, serving as the interface core for our entire ERP suite.
            </p>
          </div>
          
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 gap-3 min-w-[240px] sm:min-w-[280px]">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 backdrop-blur-sm">
              <div className="text-[10px] text-neutral-400 font-semibold uppercase tracking-wider">Reusable Comp</div>
              <div className="font-display font-extrabold text-2xl text-blum-orange mt-1">37+</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 backdrop-blur-sm">
              <div className="text-[10px] text-neutral-400 font-semibold uppercase tracking-wider">Active Version</div>
              <div className="font-display font-extrabold text-2xl text-blue-400 mt-1">v2.4.0</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="flex border-b border-slate-200">
        {tabsConfig.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 font-display font-semibold text-sm transition-all border-b-2 -mb-[2px] ${
              activeTab === tab.id 
                ? 'border-blum-orange text-blum-orange font-bold' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── TAB 1: OVERVIEW & TOKENS ─────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Color Palette Swatches */}
          <div className="xl:col-span-2 card p-6 space-y-5 bg-white">
            <div>
              <SectionTitle>Color Archetypes</SectionTitle>
              <SectionSubtitle>Click any palette to copy the hexadecimal code directly.</SectionSubtitle>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { name: 'Brand Orange', hex: '#E8530A', desc: 'Primary CTA color', bg: 'bg-[#E8530A]' },
                { name: 'Orange Light', hex: '#FF6B2B', desc: 'Hover highlights', bg: 'bg-[#FF6B2B]' },
                { name: 'Verve Dark', hex: '#1A1A1A', desc: 'Primary typography', bg: 'bg-[#1A1A1A]' },
                { name: 'Soft Charcoal', hex: '#2D2D2D', desc: 'Secondary dark surface', bg: 'bg-[#2D2D2D]' },
                { name: 'Slate Gray', hex: '#6B7280', desc: 'Muted captions', bg: 'bg-[#6B7280]' },
                { name: 'Emerald Green', hex: '#10B981', desc: 'Success indicators', bg: 'bg-[#10B981]' },
                { name: 'Indigo Royal', hex: '#6366F1', desc: 'Primary accent blue', bg: 'bg-[#6366F1]' },
                { name: 'Clean Platinum', hex: '#F7F7F5', desc: 'App backdrop surface', bg: 'bg-[#F7F7F5]', textDark: true },
              ].map(color => (
                <div 
                  key={color.name}
                  onClick={() => handleCopyToken(color.hex, 'Color HEX')}
                  className="group cursor-pointer bg-slate-50 border border-slate-100 rounded-xl overflow-hidden hover:shadow-md transition-all active:scale-[0.98]"
                >
                  <div className={`h-20 ${color.bg} flex items-end p-2 transition-transform group-hover:scale-105`}>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded backdrop-blur-md ${color.textDark ? 'bg-black/10 text-neutral-800' : 'bg-white/20 text-white'}`}>
                      {color.hex}
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-bold text-slate-800 truncate">{color.name}</div>
                    <div className="text-[10px] text-slate-400 font-medium truncate mt-0.5">{color.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typography Preview */}
          <div className="card p-6 bg-white space-y-4">
            <div>
              <SectionTitle>Typography Hierarchy</SectionTitle>
              <SectionSubtitle>Built on top of Syne and DM Sans fonts.</SectionSubtitle>
            </div>
            
            <div className="space-y-4 bg-slate-50 rounded-xl p-4 border border-slate-100 divide-y divide-slate-200/60">
              <div className="pb-3">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Page Title (Syne 24px)</span>
                <PageTitle className="text-xl">ERP Dashboard Title</PageTitle>
              </div>
              <div className="py-3">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Section Title (Syne 18px)</span>
                <SectionTitle className="text-base">Operational Summary</SectionTitle>
              </div>
              <div className="py-3">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Highlight Label</span>
                <HighlightText className="text-xs">Highlighted Accent Text</HighlightText>
              </div>
              <div className="pt-3">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Body Text (DM Sans 13px)</span>
                <BodyText className="text-xs">
                  This is standard body copy used throughout forms, tables, and standard page layouts.
                </BodyText>
              </div>
            </div>
          </div>

          {/* Live Components Dictionary */}
          <div className="xl:col-span-3 card p-6 bg-white space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <SectionTitle>Live Directory search</SectionTitle>
                <SectionSubtitle>Filter active design modules in real-time.</SectionSubtitle>
              </div>
              <div className="w-full sm:w-72">
                <SearchBar 
                  value={searchQuery} 
                  onChange={setSearchQuery} 
                  placeholder="Filter by name or category..." 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 pt-2">
              {filteredComponents.length > 0 ? (
                filteredComponents.map(comp => (
                  <div key={comp.name} className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:border-blum-orange/20 transition-all hover:bg-white hover:shadow-sm group">
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold text-slate-800 text-sm">{comp.name}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200/50 text-slate-500 group-hover:bg-blum-orange/10 group-hover:text-blum-orange transition-colors">
                        {comp.category}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-medium mt-2 leading-relaxed h-12 overflow-hidden">
                      {comp.desc}
                    </p>
                  </div>
                ))
              ) : (
                <div className="col-span-full">
                  <EmptyState 
                    icon="🔍" 
                    title="No matching components" 
                    desc={`No modules found for "${searchQuery}"`}
                    action={
                      <Button variant="ghost" onClick={() => setSearchQuery('')} className="text-xs font-semibold">
                        Clear Search Filter
                      </Button>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 2: BASIC UI COMPONENTS ─────────────────────────────── */}
      {activeTab === 'components' && (
        <div className="space-y-6">
          {/* Action Metrics & Statuses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Interactive Buttons Sandbox */}
            <div className="card p-6 bg-white space-y-4">
              <div>
                <SectionTitle>Dynamic Interactive Buttons</SectionTitle>
                <SectionSubtitle>Displaying normal, hovering, rounded icons, and loading state animations.</SectionSubtitle>
              </div>

              <div className="space-y-6 bg-slate-50 rounded-xl p-5 border border-slate-100">
                {/* Standard Variants */}
                <div>
                  <Label className="mb-2 text-[10px] text-slate-400 tracking-wider">Button Hierarchy</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button variant="primary">Primary Hero</Button>
                    <Button variant="secondary">Secondary Calm</Button>
                    <Button variant="ghost">Minimal Ghost</Button>
                    <Button variant="danger">Destructive Action</Button>
                  </div>
                </div>

                {/* Sizes and States */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 text-[10px] text-slate-400 tracking-wider">Loading / Pending States</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="primary" loading={true}>Saving Data</Button>
                      <Button variant="secondary" disabled={true}>Blocked Input</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 text-[10px] text-slate-400 tracking-wider">Icon Integration</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="primary" className="flex items-center gap-1.5 text-xs py-2 px-3">
                        <Plus size={14} /> Add Lead
                      </Button>
                      <Button variant="secondary" className="flex items-center gap-1.5 text-xs py-2 px-3">
                        <Eye size={14} /> View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Badges & Statuses */}
            <div className="card p-6 bg-white space-y-4">
              <div>
                <SectionTitle>Status Badges & Maps</SectionTitle>
                <SectionSubtitle>Pill labels mapping CRM leads and production stages dynamically.</SectionSubtitle>
              </div>

              <div className="space-y-6 bg-slate-50 rounded-xl p-5 border border-slate-100">
                {/* Default badges */}
                <div>
                  <Label className="mb-2 text-[10px] text-slate-400 tracking-wider">Semantic Badges (Default)</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="success">Active</Badge>
                    <Badge variant="warning">Awaiting</Badge>
                    <Badge variant="danger">Rejected</Badge>
                    <Badge variant="info">Dispatched</Badge>
                    <Badge variant="neutral">Draft</Badge>
                    <Badge variant="purple" dot={true}>Premium Mode</Badge>
                  </div>
                </div>

                {/* Core business flows mapped */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 text-[10px] text-slate-400 tracking-wider">Mapped Lead CRM Statuses</Label>
                    <div className="flex flex-wrap gap-2">
                      <LeadStatusBadge status="new" />
                      <LeadStatusBadge status="interested" />
                      <LeadStatusBadge status="quotation_sent" />
                      <LeadStatusBadge status="converted" />
                      <LeadStatusBadge status="lost" />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 text-[10px] text-slate-400 tracking-wider">Mapped Project Stages</Label>
                    <div className="flex flex-wrap gap-2">
                      <ProjectStatusBadge status="planning" />
                      <ProjectStatusBadge status="materials_requested" />
                      <ProjectStatusBadge status="installation_in_progress" />
                      <ProjectStatusBadge status="completed" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metric Dashboards: Stat Cards & Donut Chart */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Stat Cards Showcase */}
            <div className="xl:col-span-2 card p-6 bg-white space-y-4">
              <div>
                <SectionTitle>Stat Metric Cards</SectionTitle>
                <SectionSubtitle>Beautiful metrics widgets supporting hover effects, icons, and collapsible details.</SectionSubtitle>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StatCard 
                  title="Quarterly Closed Deals" 
                  value="₹8.45 Lakhs" 
                  sub="vs ₹6.8L last quarter" 
                  icon={<FileCheck size={20} />} 
                  color="bg-blum-orange" 
                  trend={{ value: 24, label: 'growth' }} 
                />

                <StatCard
                  title="Average Conversion Rate"
                  value="36.8%"
                  sub="Target goal 40%"
                  icon={<TrendingUp size={20} />}
                  color="bg-blue-600"
                  trend={{ value: -3.2, label: 'last week' }}
                />

                {/* Expandable Chart Stat Card */}
                <div className="sm:col-span-2">
                  <StatCard
                    title="Design Material Budgets (Interactive)"
                    value="₹10.50 Lakhs Total"
                    sub="Click arrow to toggle chart preview"
                    icon={<Database size={20} />}
                    color="bg-indigo-600"
                    isExpanded={cardExpanded}
                    onClick={() => setCardExpanded(!cardExpanded)}
                    className="cursor-pointer hover:bg-slate-50/50"
                  >
                    <div className="h-full flex items-center justify-between pt-1">
                      <div className="w-full h-full flex items-center">
                        <DonutWithLegend data={donutData} label="Material Distribution" />
                      </div>
                    </div>
                  </StatCard>
                </div>
              </div>
            </div>

            {/* Standard Donut Chart Column */}
            <div className="card p-6 bg-white space-y-4 flex flex-col justify-between">
              <div>
                <SectionTitle>Distribution Analytics</SectionTitle>
                <SectionSubtitle>Compact Donut charts perfect for sidebar and panels.</SectionSubtitle>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 flex items-center justify-center min-h-[140px] flex-1 mt-2">
                <DonutWithLegend data={donutData} label="Design Expenses Breakdown" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 3: INPUTS & SANDBOXES ──────────────────────────────── */}
      {activeTab === 'forms' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Live Form sandbox */}
          <div className="lg:col-span-2 card p-6 bg-white space-y-5">
            <div>
              <SectionTitle>Dynamic Sandbox Form</SectionTitle>
              <SectionSubtitle>Interactive components to preview inputs, selects, and modals.</SectionSubtitle>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4 bg-slate-50 rounded-xl p-5 border border-slate-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput 
                  label="Developer Name" 
                  value={formName} 
                  onChange={setFormName} 
                  placeholder="Enter full name" 
                  required={true}
                  error={formError}
                />
                
                <FormInput 
                  label="Email Address" 
                  type="email"
                  value={formEmail} 
                  onChange={setFormEmail} 
                  placeholder="name@blum-interiors.com" 
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormSelect
                  label="Assigned System Role"
                  value={formRole}
                  onChange={setFormRole}
                  options={[
                    { value: 'designer', label: 'Lead Designer' },
                    { value: 'admin', label: 'Administrator' },
                    { value: 'accountant', label: 'Financial Accountant' },
                    { value: 'designer', label: 'Project Manager' },
                  ]}
                />

                <div className="flex flex-col justify-end pb-1.5">
                  <span className="block text-[11px] font-bold text-slate-400 tracking-wider uppercase mb-2">Overlays Sandboxes</span>
                  <div className="flex items-center gap-2">
                    <Button 
                      type="button" 
                      variant="primary" 
                      onClick={() => setIsModalOpen(true)}
                      className="text-xs py-2 px-3 flex-1 flex items-center justify-center gap-1"
                    >
                      <Sparkles size={13} /> Open Modal Portal
                    </Button>
                    <Button 
                      type="button" 
                      variant="danger" 
                      onClick={() => setIsConfirmOpen(true)}
                      className="text-xs py-2 px-3 flex-1 flex items-center justify-center gap-1"
                    >
                      <Trash2 size={13} /> Confirm Dialog
                    </Button>
                  </div>
                </div>
              </div>

              <FormTextarea 
                label="Short Developer Bio" 
                value={formBio} 
                onChange={setFormBio} 
                placeholder="Tell us about your architectural or UI work..." 
                rows={3}
              />

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200/50">
                <Button type="button" variant="ghost" onClick={() => { setFormName(''); setFormEmail(''); setFormBio(''); }}>
                  Reset Sandbox
                </Button>
                <Button type="submit" variant="primary">
                  Save Sandbox Profile
                </Button>
              </div>
            </form>
          </div>

          {/* Right column: Interactive triggers for Toast and File Upload */}
          <div className="space-y-6">
            {/* Toast Alerts Trigger Center */}
            <div className="card p-6 bg-white space-y-4">
              <div>
                <SectionTitle>Global Alert Center (Toasts)</SectionTitle>
                <SectionSubtitle>Interactive elements triggering premium system notifications.</SectionSubtitle>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={() => toast.success('Operation completed successfully!')}
                  className="flex items-center gap-2 p-3 rounded-lg border border-slate-100 bg-emerald-50 hover:bg-emerald-100/50 text-emerald-700 text-[11px] font-bold transition-all text-left shadow-sm active:scale-[0.97]"
                >
                  <CheckCircle size={15} className="shrink-0 text-emerald-600" />
                  Trigger Success
                </button>

                <button
                  onClick={() => toast.error('An unexpected server timeout occurred.')}
                  className="flex items-center gap-2 p-3 rounded-lg border border-slate-100 bg-red-50 hover:bg-red-100/50 text-red-700 text-[11px] font-bold transition-all text-left shadow-sm active:scale-[0.97]"
                >
                  <AlertCircle size={15} className="shrink-0 text-red-600" />
                  Trigger Error
                </button>

                <button
                  onClick={() => toast('This is a helpful information banner.', { icon: 'ℹ️' })}
                  className="flex items-center gap-2 p-3 rounded-lg border border-slate-100 bg-blue-50 hover:bg-blue-100/50 text-blue-700 text-[11px] font-bold transition-all text-left shadow-sm active:scale-[0.97]"
                >
                  <Info size={15} className="shrink-0 text-blue-600" />
                  Trigger Info
                </button>

                <button
                  onClick={() => {
                    const myPromise = new Promise((resolve) => setTimeout(resolve, 2000));
                    toast.promise(myPromise, {
                      loading: 'Uploading document layout...',
                      success: 'Layout saved successfully!',
                      error: 'Could not upload layout.',
                    });
                  }}
                  className="flex items-center gap-2 p-3 rounded-lg border border-slate-100 bg-purple-50 hover:bg-purple-100/50 text-purple-700 text-[11px] font-bold transition-all text-left shadow-sm active:scale-[0.97]"
                >
                  <Zap size={15} className="shrink-0 text-purple-600" />
                  Trigger Promise
                </button>
              </div>
            </div>

            {/* Interactive File Upload */}
            <div className="card p-6 bg-white space-y-4">
              <div>
                <SectionTitle>Document File Dropper</SectionTitle>
                <SectionSubtitle>Fully active, gorgeous interactive sandbox drag-and-drop zone.</SectionSubtitle>
              </div>
              <FileUpload 
                onFilesSelected={(files) => toast.success(`Registered ${files.length} document layouts!`)} 
              />
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL PORTAL PORTLET ───────────────────────────────────── */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title="Interactive Sandbox Portal" 
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex gap-3 text-indigo-700 text-xs">
            <Sparkles size={20} className="shrink-0 text-indigo-600" />
            <div>
              <span className="font-bold block mb-0.5">Portal-mounted layout</span>
              Renders using React Portals to guarantee z-index layering safety. Standard backdrop blur and focus-escapes included.
            </div>
          </div>

          <div className="space-y-3">
            <FormInput label="Developer Focus Field" placeholder="Start typing here..." />
            <FormSelect 
              label="Interactive selection" 
              options={[
                { value: 'yes', label: 'This Modal works perfectly!' },
                { value: 'superb', label: 'Extremely high design standard!' }
              ]} 
            />
          </div>

          <div className="flex gap-2.5 pt-3 border-t border-slate-100">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} className="flex-1 text-xs">
              Close Portal
            </Button>
            <Button variant="primary" onClick={() => { toast.success("Modal action confirmed!"); setIsModalOpen(false); }} className="flex-1 text-xs">
              Submit Action
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── CONFIRMATION PORTAL ────────────────────────────────────── */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        title="Discard sandbox modifications?"
        message="Are you completely sure you want to discard your sandbox form modifications? This action cannot be undone."
        confirmText="Discard Modifications"
        cancelText="Cancel"
        onConfirm={() => { toast.success("Modifications successfully discarded!"); setIsConfirmOpen(false); }}
        onCancel={() => setIsConfirmOpen(false)}
        variant="danger"
      />
    </div>
  );
};
export default DesignSystemPage;
